import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';

interface StoryData {
  id: string;
  story_type: 'image' | 'video';
  story_timestamp: string;
  story_url: string;
  media_url: string | null;
  story_content: any;
  detected_keywords: string[];
  hashtags: string[];
  mentions: string[];
  links: string[];
  stickers: any[];
  music: any;
  location: any;
  repost_stories: any[];
}

interface AnalysisResult {
  username: string;
  full_name: string;
  profile_pic: string;
  follower_count: number;
  following_count: number;
  stories: StoryData[];
  total_stories: number;
  analysis_timestamp: string;
}

const StoryAnalysis: React.FC = () => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [downloadingStoryId, setDownloadingStoryId] = useState<string | null>(null);

  const INSTAGRAM_RESERVED_SEGMENTS = new Set(['p', 'reel', 'reels', 'explore', 'tv', 'accounts', 'legal', 'stories', 'share', 'direct']);

  // Profil / story linkinden kullanıcı adı çıkar
  const extractUsername = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return '';

    const storyMatch = trimmed.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/stories\/([^/?\s#]+)/i);
    if (storyMatch) {
      return storyMatch[1].replace('@', '');
    }

    const instagramUrlRegex = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?\s#]+)/i;
    const match = trimmed.match(instagramUrlRegex);
    if (match) {
      const seg = match[1].replace('@', '');
      if (INSTAGRAM_RESERVED_SEGMENTS.has(seg.toLowerCase())) return '';
      return seg;
    }

    return trimmed.replace('@', '').trim();
  };

  const handleDownloadStoryMedia = async (story: StoryData, index: number) => {
    if (!story.media_url) {
      setError('Bu hikaye için indirilebilir medya adresi yok.');
      return;
    }
    if (!result) return;

    setDownloadingStoryId(story.id);
    setError('');
    try {
      const proxyUrl = `/api/proxy/image?url=${encodeURIComponent(story.media_url)}`;
      const token = localStorage.getItem('token');
      const response = await axios.get(proxyUrl, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      const ext = story.story_type === 'video' ? 'mp4' : 'jpg';
      const safeTs = story.story_timestamp.replace(/[:.]/g, '-').slice(0, 25);
      const filename = `${result.username}-hikaye-${index + 1}-${safeTs}.${ext}`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Hikaye indirme hatası:', err);
      setError('Medya indirilemedi (bağlantı süresi dolmuş veya Instagram engeli olabilir).');
    } finally {
      setDownloadingStoryId(null);
    }
  };

  const handleSearch = async () => {
    const cleanUsername = extractUsername(username);
    
    if (!cleanUsername) {
      setError('Lütfen geçerli bir kullanıcı adı veya profil linki girin');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.get(`/api/tools/story-analysis?username=${encodeURIComponent(cleanUsername)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || 'Analiz yapılamadı');
      }
    } catch (err: any) {
      console.error('Story analiz hatası:', err);
      setError(err.response?.data?.message || 'Sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (username.trim()) {
      handleSearch();
    }
  };

  const handleClear = () => {
    setUsername('');
    setError('');
    setResult(null);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('tr-TR');
  };

  const getStoryIcon = (type: string) => {
    return type === 'video' ? <VideoIcon /> : <ImageIcon />;
  };

  const getStoryTypeColor = (type: string) => {
    return type === 'video' ? 'primary' : 'secondary';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ 
        fontSize: { xs: '1.75rem', sm: '2.125rem' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Story Analiz
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ 
        mb: 3,
        textAlign: { xs: 'center', sm: 'left' },
        fontSize: { xs: '0.9rem', sm: '1rem' }
      }}>
        Instagram kullanıcı adını veya profil linkini girerek paylaştığı hikayeleri, etiketleri, linkleri ve hashtag'leri analiz edin.
      </Typography>

      {/* Arama Kutusu */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <TextField
              fullWidth
              label="Instagram Kullanıcı Adı veya Profil Linki"
              placeholder="örn: kullanici_adi, profil veya story linki (instagram.com/stories/kullanici/...)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              disabled={loading}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: username && !loading && (
                  <Tooltip title="Temizle">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ mr: 1 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }}
              sx={{ mb: { xs: 2, sm: 0 } }}
            />
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || !username.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ 
                  minWidth: { xs: '100%', sm: 120 },
                  flex: { xs: 1, sm: 'none' }
                }}
              >
                {loading ? 'Analiz Ediliyor...' : 'Analiz Et'}
              </Button>
              {result && (
                <Tooltip title="Yenile">
                  <IconButton 
                    onClick={handleRefresh} 
                    disabled={loading}
                    sx={{ 
                      display: { xs: 'none', sm: 'flex' },
                      minWidth: 48
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Hata Mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Sonuçlar */}
      {result && (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Profil Bilgileri */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Avatar
                    src={result.profile_pic}
                    sx={{ 
                      width: { xs: 80, sm: 60 }, 
                      height: { xs: 80, sm: 60 }, 
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 1, sm: 0 }
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                      {result.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{result.username}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Toplam Hikaye:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {result.total_stories}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Analiz Zamanı: {formatTimestamp(result.analysis_timestamp)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Hikayeler */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Paylaşılan Hikayeler ({result.stories.length})
                </Typography>
                
                {result.stories.length === 0 ? (
                  <Alert severity="info">
                    Bu kullanıcının henüz hikayesi bulunmuyor.
                  </Alert>
                ) : (
                  <List>
                    {result.stories.map((story, index) => (
                      <React.Fragment key={story.id}>
                        <ListItem alignItems="flex-start" sx={{ 
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'flex-start' }
                        }}>
                          <ListItemIcon sx={{ 
                            minWidth: { xs: 'auto', sm: 56 },
                            mb: { xs: 1, sm: 0 }
                          }}>
                            {getStoryIcon(story.story_type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                mb: 1,
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' }
                              }}>
                                <Chip
                                  label={story.story_type === 'video' ? 'Video' : 'Fotoğraf'}
                                  color={getStoryTypeColor(story.story_type)}
                                  size="small"
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}>
                                  {formatTimestamp(story.story_timestamp)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                {/* Hashtag'ler */}
                                {story.hashtags && story.hashtags.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Hashtag'ler:
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap', 
                                      gap: 0.5,
                                      justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                                    }}>
                                      {story.hashtags.map((hashtag, idx) => (
                                        <Chip
                                          key={idx}
                                          label={`#${hashtag}`}
                                          size="small"
                                          color="primary"
                                          variant="filled"
                                          onClick={() => window.open(`https://www.instagram.com/explore/tags/${hashtag}/`, '_blank')}
                                          sx={{ 
                                            cursor: 'pointer',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            height: { xs: 24, sm: 28 },
                                            '&:hover': {
                                              backgroundColor: 'primary.dark',
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Mention'lar */}
                                {story.mentions && story.mentions.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Mention'lar:
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap', 
                                      gap: 0.5,
                                      justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                                    }}>
                                      {story.mentions.map((mention, idx) => (
                                        <Chip
                                          key={idx}
                                          label={`@${mention}`}
                                          size="small"
                                          color="secondary"
                                          variant="filled"
                                          icon={<PersonIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                                          onClick={() => window.open(`https://www.instagram.com/${mention}/`, '_blank')}
                                          sx={{ 
                                            cursor: 'pointer',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            height: { xs: 24, sm: 28 },
                                            '&:hover': {
                                              backgroundColor: 'secondary.dark',
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Linkler */}
                                {story.links && story.links.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Linkler:
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap', 
                                      gap: 0.5,
                                      justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                                    }}>
                                      {story.links.map((link, idx) => (
                                        <Chip
                                          key={idx}
                                          label={link.length > 30 ? `${link.substring(0, 30)}...` : link}
                                          size="small"
                                          color="primary"
                                          variant="filled"
                                          icon={<LinkIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                                          onClick={() => window.open(link.startsWith('http') ? link : `https://${link}`, '_blank')}
                                          sx={{ 
                                            cursor: 'pointer',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            height: { xs: 24, sm: 28 },
                                            maxWidth: { xs: '100%', sm: '300px' },
                                            textDecoration: 'underline',
                                            '&:hover': {
                                              backgroundColor: 'primary.dark',
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Tespit Edilen Anahtar Kelimeler */}
                                {story.detected_keywords && story.detected_keywords.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Tespit Edilen Anahtar Kelimeler:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {story.detected_keywords.map((keyword, idx) => (
                                        <Chip
                                          key={idx}
                                          label={keyword}
                                          size="small"
                                          color="warning"
                                          variant="filled"
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Sticker'lar */}
                                {story.stickers && story.stickers.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Sticker'lar ({story.stickers.length}):
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {story.stickers.map((sticker: any, idx: number) => 
                                        sticker.text || sticker.emoji || 'Sticker'
                                      ).join(', ')}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Müzik */}
                                {story.music && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Müzik:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {story.music.title || 'Bilinmeyen Müzik'}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Konum */}
                                {story.location && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Konum:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {story.location.name || 'Bilinmeyen Konum'}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Repost Hikayeler */}
                                {story.repost_stories && story.repost_stories.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Repost Hikayeler ({story.repost_stories.length}):
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {story.repost_stories.map((repost, idx) => (
                                        <Chip
                                          key={idx}
                                          label={repost.media_code}
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                          onClick={() => window.open(repost.story_url, '_blank')}
                                          sx={{ cursor: 'pointer' }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Hikaye linki ve indirme */}
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                  <Button
                                    size="small"
                                    startIcon={<ShareIcon />}
                                    onClick={() => window.open(story.story_url, '_blank')}
                                  >
                                    Hikayeyi Görüntüle
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      downloadingStoryId === story.id ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : (
                                        <DownloadIcon />
                                      )
                                    }
                                    disabled={!story.media_url || downloadingStoryId === story.id}
                                    onClick={() => handleDownloadStoryMedia(story, index)}
                                  >
                                    {downloadingStoryId === story.id ? 'İndiriliyor...' : 'Medyayı İndir'}
                                  </Button>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < result.stories.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StoryAnalysis;
